import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, User, ArrowRight, Loader2 } from 'lucide-react';
import API from '../api/axios';
import { AppContext } from '../context/AppContext';

const AuthModal = ({ isOpen, onClose }) => {
    const [isLogin, setIsLogin] = useState(true);
    const [loading, setLoading] = useState(false);
    const { setUser } = useContext(AppContext);
    
    // Explicitly structure the state
    const [formData, setFormData] = useState({ 
        name: '', 
        email: '', 
        password: '',
        role: 'customer' // Default role for registration
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        const endpoint = isLogin ? '/api/auth/login' : '/api/auth/register';
        
        // Construct the payload dynamically depending on the active state
        const payload = isLogin 
            ? { email: formData.email.trim(), password: formData.password }
            : { 
                name: formData.name.trim(), 
                email: formData.email.trim(), 
                password: formData.password, 
                role: formData.role 
              };
        
        try {
            const res = await API.post(`${endpoint}`, payload);
            
            if (isLogin) {
                // Store both token and user object
                localStorage.setItem('token', res.data.token);
                localStorage.setItem('user', JSON.stringify(res.data));
                setUser(res.data);
                onClose();
            } else {
                // After registration, flip to login mode automatically
                setIsLogin(true);
                alert("Account created successfully! Please sign in.");
            }
        } catch (err) {
            alert(err.response?.data?.error || "Authentication failed. Please check your details.");
        } finally {
            setLoading(false);
        }
    };

    // Helper to toggle auth state and sweep dirty data fields clean
    const handleModeToggle = () => {
        setIsLogin(!isLogin);
        setFormData({ name: '', email: '', password: '', role: 'customer' });
    };

    if (!isOpen) return null;

    return (
        <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="fixed inset-0 z-[100] flex items-center justify-center backdrop-blur-2xl bg-black/60 p-4"
        >
            <motion.div 
                initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }}
                className="relative w-full max-w-md bg-white/90 dark:bg-black/80 p-8 rounded-5xl border border-white/20 shadow-2xl"
            >
                <button onClick={onClose} className="absolute top-6 right-6 p-2 hover:bg-red-500/10 rounded-full transition-colors"><X /></button>
                
                <h2 className="text-3xl font-black mb-2">{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                <p className="text-sm opacity-60 mb-8">{isLogin ? 'Sign in to sync your cart across devices.' : 'Join the high-speed tech community.'}</p>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                        <div className="relative">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                            <input 
                                type="text" required placeholder="Full Name" 
                                value={formData.name}
                                className="w-full bg-black/5 dark:bg-white/5 p-4 pl-12 rounded-2xl outline-none focus:ring-2 focus:ring-green-500/50 transition-all"
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                            />
                        </div>
                    )}
                    <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input 
                            type="email" required placeholder="Email Address" 
                            value={formData.email}
                            className="w-full bg-black/5 dark:bg-white/5 p-4 pl-12 rounded-2xl outline-none focus:ring-2 focus:ring-red-500/50 transition-all"
                            onChange={(e) => setFormData({...formData, email: e.target.value})}
                        />
                    </div>
                    <div className="relative">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input 
                            type="password" required placeholder="Password" 
                            value={formData.password}
                            className="w-full bg-black/5 dark:bg-white/5 p-4 pl-12 rounded-2xl outline-none focus:ring-2 focus:ring-red-500/50 transition-all"
                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                        />
                    </div>

                    <button 
                        disabled={loading}
                        type="submit" 
                        className="w-full bg-red-600 hover:bg-red-700 text-white py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                    >
                        {loading ? <Loader2 className="animate-spin" /> : (isLogin ? 'Sign In' : 'Register')} <ArrowRight size={18} />
                    </button>
                </form>

                <p className="mt-6 text-center text-xs font-medium">
                    {isLogin ? "New here?" : "Already have an account?"} 
                    <button onClick={handleModeToggle} className="ml-2 text-green-500 font-bold hover:underline underline-offset-4">
                        {isLogin ? 'Create one now' : 'Log in here'}
                    </button>
                </p>
            </motion.div>
        </motion.div>
    );
};

export default AuthModal;